import { renderTermBlock } from "./dom-render";
import {
  fromHtml,
  fromHtmlElement,
  fromJson,
  fromMarkdown,
  type HtmlAction,
} from "./document";
import { defaultThemeSpec, resolveTheme, themeToCssVars } from "./theme";
import type { LayoutFn, Theme, ThemeMode, ThemePreference } from "./termui";

const DEFAULT_COLS = 80;
const PROBE_SAMPLE = "0".repeat(120);
type SourceFormat = "html" | "markdown" | "json";
const TERM_UI_CSS = `
:host {
  display: block;
}
@keyframes bk { 50% { opacity: 0 } }
.termui-frame {
  --cell-h: 1.15em;
  --termui-bg: #0c0c0c;
  --termui-fg: #cccccc;
  font-family: "Iosevka Term", "JetBrains Mono", Menlo, Monaco, Consolas, "Liberation Mono", monospace;
  font-size: inherit;
  line-height: var(--cell-h);
  letter-spacing: inherit;
  white-space: pre;
  overflow: clip;
  background: var(--termui-bg);
  color: var(--termui-fg);
  user-select: text;
}
.termui-root {
  white-space: pre;
  position: relative;
}
.termui-line {
  line-height: var(--cell-h);
  min-height: var(--cell-h);
}
.termui-clickable {
  cursor: pointer;
}
.termui-clickable:focus-visible {
  outline: 1px solid var(--termui-info, currentColor);
  outline-offset: 1px;
}
.termui-scroll-region {
  overflow: hidden;
}
.termui-media {
  pointer-events: auto;
}
.termui-probe {
  font: inherit;
  white-space: pre;
  visibility: hidden;
  position: absolute;
  pointer-events: none;
  inset: -9999px auto auto -9999px;
}
`;

export type TermUIActionEvent = CustomEvent<HtmlAction>;

function readSystemMode(): ThemeMode {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return "dark";
  }
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function parseMode(raw: string | null): ThemePreference {
  if (!raw) return "system";
  const mode = raw.trim().toLowerCase();
  if (mode === "light" || mode === "dark" || mode === "system") return mode;
  throw new Error(`Invalid mode "${raw}". Expected "light", "dark", or "system".`);
}

function parseWidth(raw: string | null): number | undefined {
  if (!raw) return undefined;
  const parsed = Number.parseInt(raw, 10);
  if (Number.isFinite(parsed) && parsed > 0) return parsed;
  throw new Error(`Invalid width "${raw}". Expected a positive integer.`);
}

function parseSourceFormat(raw: string | null): SourceFormat {
  if (!raw) return "html";
  const format = raw.trim().toLowerCase();
  if (format === "markdown" || format === "json" || format === "html") return format;
  throw new Error(`Invalid source-format "${raw}". Expected "html", "markdown", or "json".`);
}

function readScriptText(host: Element, types: string[]): string | undefined {
  for (const type of types) {
    const node = host.querySelector(`script[type="${type}"]`);
    if (!node) continue;
    return node.textContent ?? "";
  }
  return undefined;
}

function parseJsonSource(raw: string): unknown {
  const text = raw.trim();
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch (error) {
    const message = error instanceof Error ? error.message : "Invalid JSON";
    throw new Error(`Invalid JSON source: ${message}`);
  }
}

function actionEvent(action: HtmlAction): TermUIActionEvent {
  return new CustomEvent<HtmlAction>("termui-action", {
    detail: action,
    bubbles: true,
    composed: true,
  });
}

export class TermUIElement extends HTMLElement {
  static observedAttributes = ["width", "mode", "source", "source-format", "json-title"] as const;

  private readonly frame: HTMLDivElement;
  private readonly root: HTMLDivElement;
  private layoutOverride?: LayoutFn;
  private resizeObserver?: ResizeObserver;
  private mutationObserver?: MutationObserver;
  private observedShadowRoots = new WeakSet<ShadowRoot>();
  private mediaQuery?: MediaQueryList;
  private mediaQueryListener?: (event: MediaQueryListEvent) => void;
  private renderQueued = false;
  private renderFrameId: number | undefined;

  constructor() {
    super();
    const shadow = this.attachShadow({ mode: "open" });
    const style = document.createElement("style");
    style.textContent = TERM_UI_CSS;

    this.frame = document.createElement("div");
    this.frame.className = "termui-frame";
    this.root = document.createElement("div");
    this.root.className = "termui-root";
    this.frame.appendChild(this.root);
    shadow.append(style, this.frame);
  }

  connectedCallback(): void {
    this.resizeObserver = new ResizeObserver(() => this.queueRender());
    this.resizeObserver.observe(this);

    this.mutationObserver = new MutationObserver(() => {
      this.observeOpenShadowRoots();
      this.queueRender();
    });
    this.mutationObserver.observe(this, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
    });
    this.observeOpenShadowRoots();

    if (typeof window !== "undefined" && typeof window.matchMedia === "function") {
      this.mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
      this.mediaQueryListener = () => {
        if (parseMode(this.getAttribute("mode")) === "system") this.queueRender();
      };
      this.mediaQuery.addEventListener("change", this.mediaQueryListener);
    }

    this.queueRender();
  }

  disconnectedCallback(): void {
    if (this.renderFrameId !== undefined) {
      cancelAnimationFrame(this.renderFrameId);
      this.renderFrameId = undefined;
      this.renderQueued = false;
    }
    this.resizeObserver?.disconnect();
    this.resizeObserver = undefined;
    this.mutationObserver?.disconnect();
    this.mutationObserver = undefined;
    this.observedShadowRoots = new WeakSet<ShadowRoot>();
    if (this.mediaQuery && this.mediaQueryListener) {
      this.mediaQuery.removeEventListener("change", this.mediaQueryListener);
    }
    this.mediaQuery = undefined;
    this.mediaQueryListener = undefined;
  }

  attributeChangedCallback(): void {
    this.queueRender();
  }

  render(): void {
    this.queueRender();
  }

  get layout(): LayoutFn | undefined {
    return this.layoutOverride;
  }

  set layout(next: LayoutFn | null | undefined) {
    this.layoutOverride = next ?? undefined;
    this.queueRender();
  }

  private queueRender(): void {
    if (this.renderQueued) return;
    if (typeof requestAnimationFrame !== "function") {
      throw new Error("requestAnimationFrame is unavailable; term-ui rendering requires a browser frame loop.");
    }
    this.renderQueued = true;
    this.renderFrameId = requestAnimationFrame(() => {
      this.renderQueued = false;
      this.renderFrameId = undefined;
      this.renderNow();
    });
  }

  private observeOpenShadowRoots(): void {
    if (!this.mutationObserver) return;
    const stack: Element[] = Array.from(this.children);

    while (stack.length > 0) {
      const current = stack.pop()!;

      for (const child of Array.from(current.children)) {
        stack.push(child);
      }

      if (!(current instanceof HTMLElement)) continue;
      const shadowRoot = current.shadowRoot;
      if (!shadowRoot || shadowRoot.mode !== "open") continue;
      if (!this.observedShadowRoots.has(shadowRoot)) {
        this.mutationObserver.observe(shadowRoot, {
          childList: true,
          subtree: true,
          characterData: true,
          attributes: true,
        });
        this.observedShadowRoots.add(shadowRoot);
      }

      for (const shadowChild of Array.from(shadowRoot.children)) {
        stack.push(shadowChild);
      }
    }
  }

  private onAction = (action: HtmlAction): void => {
    this.dispatchEvent(actionEvent(action));
  };

  private measureCharacterWidth(): number {
    const probe = document.createElement("span");
    probe.className = "termui-probe";
    probe.textContent = PROBE_SAMPLE;
    this.frame.appendChild(probe);
    const width = probe.getBoundingClientRect().width / PROBE_SAMPLE.length;
    probe.remove();
    return width;
  }

  private resolveColumns(): number {
    const explicit = parseWidth(this.getAttribute("width"));
    if (explicit) return explicit;

    const charWidth = this.measureCharacterWidth();
    if (!Number.isFinite(charWidth) || charWidth <= 0) return DEFAULT_COLS;

    const available = this.clientWidth;
    if (!Number.isFinite(available) || available <= 0) return DEFAULT_COLS;
    return Math.max(1, Math.floor(available / charWidth));
  }

  private resolveTheme(themeMode: ThemePreference): Theme {
    const theme = resolveTheme(defaultThemeSpec, themeMode, { systemMode: readSystemMode() });
    const cssVars = themeToCssVars(theme);
    for (const [name, value] of Object.entries(cssVars)) {
      this.frame.style.setProperty(name, value);
    }
    return theme;
  }

  private renderNow(): void {
    const mode = parseMode(this.getAttribute("mode"));
    const sourceFormat = parseSourceFormat(this.getAttribute("source-format"));
    const cols = this.resolveColumns();
    this.frame.style.width = `${cols}ch`;

    const theme = this.resolveTheme(mode);
    const source = this.getAttribute("source");
    const layout = (() => {
      if (this.layoutOverride) return this.layoutOverride;

      if (source !== null) {
        if (sourceFormat === "markdown") {
          return fromMarkdown(source, { theme });
        }
        if (sourceFormat === "json") {
          return fromJson(parseJsonSource(source), {
            theme,
            title: this.getAttribute("json-title") ?? undefined,
          });
        }
        return fromHtml(source, { theme, onAction: this.onAction });
      }

      if (sourceFormat === "markdown") {
        const scriptSource = readScriptText(this, ["text/markdown"]);
        const markdown = scriptSource ?? this.textContent ?? "";
        return fromMarkdown(markdown, { theme });
      }

      if (sourceFormat === "json") {
        const scriptSource = readScriptText(this, ["application/json"]);
        const jsonText = scriptSource ?? this.textContent ?? "";
        return fromJson(parseJsonSource(jsonText), {
          theme,
          title: this.getAttribute("json-title") ?? undefined,
        });
      }

      return fromHtmlElement(this, { theme, onAction: this.onAction });
    })();
    const block = layout(cols);
    renderTermBlock(this.root, block, theme);
  }
}

export const TERM_UI_TAG = "term-ui";

export function registerTermUIElement(tagName = TERM_UI_TAG): void {
  if (typeof customElements === "undefined") return;
  if (customElements.get(tagName)) return;
  customElements.define(tagName, TermUIElement);
}
